import React, { useState } from "react";
import { api } from "../../lib/api";
import { Avatar, ReplaIdModal, ConfirmModal } from "../common";
import { EditNpcModal } from "./CreationModals";
import { Plus, Pencil, Power, Search, RefreshCw, Trash2 } from "lucide-react";

const TYPE_LABEL = { npc: "PNJ", institution: "INSTITUTION", anonymous: "ANONYME", news_source: "SOURCE INFO" };

export default function NpcManager({ npcs, orgs, campaignId, onReload, onNew, onOrgAdded }) {
  const [editing, setEditing] = useState(null);
  const [idEditing, setIdEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("name");
  const list = npcs
    .filter((n) => n.name.toLowerCase().includes(q.toLowerCase()) || (n.organization || "").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      if (sort === "organization") {
        const c = (a.organization || "~").localeCompare(b.organization || "~");
        if (c !== 0) return c;
      }
      return (a.name || "").localeCompare(b.name || "");
    });

  async function toggleActive(n) {
    await api.patch(`/npcs/${n.id}`, { is_active: !n.is_active });
    onReload?.();
  }
  async function toggleOffline(n) {
    await api.patch(`/npcs/${n.id}`, { force_offline: !n.force_offline });
    onReload?.();
  }
  async function regenNpcId(n) {
    setIdEditing(n);
  }
  async function confirmRemove() {
    await api.delete(`/npcs/${toDelete.id}`);
    setToDelete(null);
    onReload?.();
  }

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ background: "var(--r-void)" }}>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="sys-label">RÉPERTOIRE</div>
            <h2 className="font-title font-bold text-xl tracking-tight mt-1">IDENTITÉS PNJ · {npcs.length}</h2>
          </div>
          <button data-testid="npcmanager-new" className="btn btn-solid flex items-center gap-2" onClick={onNew}><Plus size={14} /> NOUVEAU PNJ</button>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          <div className="relative flex-1" style={{ minWidth: 200, maxWidth: 320 }}>
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--r-text-muted)] pointer-events-none" />
            <input data-testid="npc-search" className="input font-mono text-xs" style={{ paddingLeft: 36 }} placeholder="RECHERCHER UN PNJ" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="npc-sort" className="input font-mono text-xs" style={{ width: "auto" }} value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="name">TRI : NOM</option>
            <option value="organization">TRI : ORGANISATION</option>
          </select>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((n) => (
            <div key={n.id} data-testid={`npc-card-${n.id}`} className="frame p-3" style={{ opacity: n.is_active ? 1 : 0.5 }}>
              <div className="flex items-start gap-3">
                <div className="relative">
                  <Avatar src={n.avatar} name={n.name} size={44} square={n.type !== "npc"} />
                  <span title={n.force_offline ? "Forcé hors ligne" : "En ligne quand le MJ est connecté"}
                    className="absolute -bottom-0.5 -right-0.5 rounded-full"
                    style={{ width: 12, height: 12, border: "2px solid var(--r-panel)", background: n.force_offline ? "var(--r-text-muted)" : "#22C55E", boxShadow: n.force_offline ? "none" : "0 0 6px #22C55E" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold break-words leading-tight">{n.name}</div>
                  <div className="sys-label break-words leading-tight" style={{ textTransform: "none", letterSpacing: "normal" }}>{n.function || "—"}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="font-mono text-[9px] tracking-[0.15em] px-1.5 py-0.5" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 35%, transparent)", color: "var(--r-primary)" }}>{TYPE_LABEL[n.type] || n.type}</span>
                    {n.organization && <span className="sys-label break-words" style={{ textTransform: "none", letterSpacing: "normal" }}>{n.organization}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="sys-label">ID RÉPLA</span>
                    <span data-testid={`npc-repla-id-${n.id}`} className="font-mono text-xs px-1.5 py-0.5" style={{ color: "var(--r-primary)", border: "1px solid color-mix(in srgb, var(--r-primary) 30%, transparent)" }}>{n.repla_id || "—"}</span>
                    <button data-testid={`regen-npc-id-${n.id}`} title="Régénérer l'ID" onClick={() => regenNpcId(n)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-primary)]"><RefreshCw size={11} /></button>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-[color:var(--r-primary)]/10">
                <button data-testid={`edit-npc-${n.id}`} onClick={() => setEditing(n)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ padding: "6px 8px" }}><Pencil size={11} /> MODIFIER</button>
                <button data-testid={`presence-npc-${n.id}`} onClick={() => toggleOffline(n)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ padding: "6px 8px", color: n.force_offline ? "#22C55E" : "var(--r-text-muted)" }}>
                  {n.force_offline ? "● REMETTRE EN LIGNE" : "○ PASSER HORS LIGNE"}
                </button>
                <button data-testid={`toggle-npc-${n.id}`} onClick={() => toggleActive(n)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ padding: "6px 8px", color: n.is_active ? "var(--r-text-muted)" : "var(--r-primary)" }}>
                  <Power size={11} /> {n.is_active ? "DÉSACTIVER" : "ACTIVER"}
                </button>
                <button data-testid={`del-npc-${n.id}`} onClick={() => setToDelete(n)} className="btn btn-ghost flex-1 justify-center flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ padding: "6px 8px", color: "var(--r-alert)" }}>
                  <Trash2 size={11} /> SUPPRIMER
                </button>
              </div>
            </div>
          ))}
        </div>
        {npcs.length === 0 && <div className="sys-label">AUCUN PNJ</div>}
      </div>

      <EditNpcModal open={!!editing} onClose={() => setEditing(null)} npc={editing} campaignId={campaignId} orgs={orgs} onOrgAdded={onOrgAdded} onSaved={onReload} />
      <ReplaIdModal
        open={!!idEditing}
        current={idEditing?.repla_id}
        onClose={() => setIdEditing(null)}
        onSubmit={async (val) => { await api.post(`/npcs/${idEditing.id}/regenerate-id`, { repla_id: val }); onReload?.(); }}
      />
      <ConfirmModal
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={confirmRemove}
        title="SUPPRIMER LE PNJ"
        confirmLabel="SUPPRIMER"
        message={toDelete ? `Supprimer le PNJ « ${toDelete.name} » ? Il sera retiré de toutes les conversations où il apparaît.` : ""}
      />
    </div>
  );
}
